import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authAPI, User, LoginCredentials, RegisterData } from '../lib/auth-api'
import { onAuthChange, hasValidToken } from '../lib/authClient'

// Demo mode for UI testing without backend
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true' || import.meta.env.DEV

export interface OnboardingState {
  step: number
  pendingEmail: string | null
  emailVerified: boolean
  registrationToken: string | null
}

export interface AuthState {
  // User state
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Onboarding state
  onboarding: OnboardingState

  // MFA state
  mfaRequired: boolean
  mfaChallengeId: string | null

  // Actions - Authentication
  login: (credentials: LoginCredentials) => Promise<void>
  loginWithPasskey: () => Promise<void>
  loginWithApple: () => Promise<void>
  loginWithOAuth: (provider: 'google' | 'github') => void
  logout: () => Promise<void>
  refreshAuth: () => Promise<void>
  getCurrentUser: () => Promise<void>
  initializeSession: () => Promise<void>

  // Actions - Registration/Onboarding
  startRegistration: (email: string) => Promise<void>
  verifyEmail: (code: string) => Promise<void>
  completeRegistration: (data: { password?: string; usePasskey?: boolean }) => Promise<void>
  registerPasskey: () => Promise<void>
  setOnboardingStep: (step: number) => void
  resetOnboarding: () => void

  // Actions - MFA
  completeMFA: (code: string) => Promise<void>

  // Actions - Wallet / Device QR login
  loginWithToken: (token: string) => Promise<void>

  // Actions - Utility
  clearError: () => void
  setError: (error: string) => void
}

const initialOnboardingState: OnboardingState = {
  step: 1,
  pendingEmail: null,
  emailVerified: false,
  registrationToken: null,
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      onboarding: initialOnboardingState,
      mfaRequired: false,
      mfaChallengeId: null,

      // Login with email/password
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null })
        try {
          const result = await authAPI.login(credentials)

          if (result.mfaRequired) {
            set({
              isLoading: false,
              mfaRequired: true,
              mfaChallengeId: result.mfaRequired.challengeId,
            })
            return
          }

          set({
            user: result.user,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            mfaChallengeId: null,
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Login failed',
          })
          throw error
        }
      },

      // Login with passkey (WebAuthn)
      loginWithPasskey: async () => {
        set({ isLoading: true, error: null })
        try {
          // Import dynamically to avoid SSR issues
          const { startAuthentication } = await import('@simplewebauthn/browser')

          // Get authentication options from server
          const options = await authAPI.beginWebAuthnAuthentication()

          // Prompt user for passkey
          const credential = await startAuthentication(options)

          // Complete authentication
          const result = await authAPI.completeWebAuthnAuthentication(credential)

          set({
            user: result.user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Passkey authentication failed',
          })
          throw error
        }
      },

      // Apple Sign In — uses native JS SDK popup (no redirect)
      loginWithApple: async () => {
        set({ isLoading: true, error: null })
        try {
          const { signInWithApple, exchangeAppleCode } = await import('../lib/apple-auth')
          const appleResponse = await signInWithApple()
          const result = await exchangeAppleCode(
            appleResponse.authorization.code,
            appleResponse.authorization.id_token,
            appleResponse.user
          )
          if (!result.ok || !result.token) {
            throw new Error(result.error || 'Apple Sign In failed')
          }
          const user = await authAPI.loginWithAppleToken(result.token)
          set({ user, isAuthenticated: true, isLoading: false })
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'Apple Sign In failed'
          // Ignore user-cancelled popup (popup_closed_by_user)
          if (msg !== 'popup_closed_by_user') {
            set({ isLoading: false, error: msg })
          } else {
            set({ isLoading: false })
          }
          throw error
        }
      },

      // OAuth login - redirects to provider (Google, GitHub)
      loginWithOAuth: (provider: 'google' | 'github') => {
        const redirectUri = `${window.location.origin}/oauth/callback`
        const url = authAPI.getOAuthURL(provider as 'google' | 'github' | 'microsoft', redirectUri)
        window.location.href = url
      },

      // Logout
      logout: async () => {
        set({ isLoading: true })
        try {
          await authAPI.logout()
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            onboarding: initialOnboardingState,
            mfaRequired: false,
            mfaChallengeId: null,
          })
        }
      },

      // Refresh authentication
      refreshAuth: async () => {
        try {
          await authAPI.refreshToken()
          const user = await authAPI.getCurrentUser()
          set({ user, isAuthenticated: true })
        } catch {
          set({ user: null, isAuthenticated: false })
        }
      },

      // Get current user
      getCurrentUser: async () => {
        set({ isLoading: true })
        try {
          const user = await authAPI.getCurrentUser()
          set({ user, isAuthenticated: true, isLoading: false })
        } catch {
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },

      // Initialize session from HttpOnly cookie on app startup
      initializeSession: async () => {
        set({ isLoading: true })
        try {
          // Try to restore session from HttpOnly refresh cookie
          const hasSession = await authAPI.initializeSession()
          if (hasSession) {
            // Get user profile after successful token refresh
            const user = await authAPI.getCurrentUser()
            set({ user, isAuthenticated: true, isLoading: false })
          } else {
            set({ user: null, isAuthenticated: false, isLoading: false })
          }
        } catch {
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },

      // Start registration - sends verification email
      startRegistration: async (email: string) => {
        set({ isLoading: true, error: null })

        // Demo mode - skip API call for UI testing
        if (DEMO_MODE) {
          await new Promise(resolve => setTimeout(resolve, 800)) // Simulate network delay
          set({
            isLoading: false,
            onboarding: {
              ...get().onboarding,
              step: 2,
              pendingEmail: email,
              registrationToken: 'demo-token-' + Date.now(),
            },
          })
          return
        }

        try {
          // Call registration init endpoint
          const response = await fetch(
            `${import.meta.env.VITE_AUTH_API_URL || 'https://api.lukhas.id/v1'}/auth/register/init`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email }),
            }
          )

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.message || 'Registration failed')
          }

          const data = await response.json()

          set({
            isLoading: false,
            onboarding: {
              ...get().onboarding,
              step: 2,
              pendingEmail: email,
              registrationToken: data.token,
            },
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Registration failed',
          })
          throw error
        }
      },

      // Verify email with 6-digit code
      verifyEmail: async (code: string) => {
        set({ isLoading: true, error: null })
        const { onboarding } = get()

        // Demo mode - accept any 6-digit code
        if (DEMO_MODE) {
          await new Promise(resolve => setTimeout(resolve, 600))
          if (code.length !== 6) {
            set({ isLoading: false, error: 'Please enter a 6-digit code' })
            throw new Error('Invalid code')
          }
          set({
            isLoading: false,
            onboarding: {
              ...onboarding,
              step: 3,
              emailVerified: true,
            },
          })
          return
        }

        try {
          const response = await fetch(
            `${import.meta.env.VITE_AUTH_API_URL || 'https://api.lukhas.id/v1'}/auth/register/verify`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                token: onboarding.registrationToken,
                code,
              }),
            }
          )

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.message || 'Invalid verification code')
          }

          set({
            isLoading: false,
            onboarding: {
              ...onboarding,
              step: 3,
              emailVerified: true,
            },
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Verification failed',
          })
          throw error
        }
      },

      // Complete registration with password or passkey
      completeRegistration: async (data: { password?: string; usePasskey?: boolean }) => {
        set({ isLoading: true, error: null })
        const { onboarding } = get()

        // Demo mode - create mock user
        if (DEMO_MODE) {
          await new Promise(resolve => setTimeout(resolve, 1000))
          const email = onboarding.pendingEmail || 'demo@lukhas.id'
          const mockUser: User = {
            id: 'demo-' + Date.now(),
            email,
            username: email.split('@')[0],
            displayName: email.split('@')[0],
            verified: true,
            createdAt: new Date().toISOString(),
            roles: ['user'],
          }
          set({
            user: mockUser,
            isAuthenticated: true,
            isLoading: false,
            onboarding: {
              ...onboarding,
              step: 4,
            },
          })
          return
        }

        try {
          const registerData: RegisterData = {
            email: onboarding.pendingEmail!,
            username: onboarding.pendingEmail!.split('@')[0],
            password: data.password || '',
            acceptTerms: true,
          }

          const result = await authAPI.register(registerData)

          // If using passkey, register it after account creation
          if (data.usePasskey) {
            await get().registerPasskey()
          }

          set({
            user: result.user,
            isAuthenticated: true,
            isLoading: false,
            onboarding: {
              ...onboarding,
              step: 4, // Move to namespace education
            },
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Registration failed',
          })
          throw error
        }
      },

      // Register a new passkey
      registerPasskey: async () => {
        set({ isLoading: true, error: null })
        try {
          const { startRegistration } = await import('@simplewebauthn/browser')

          // Get registration options from server
          const options = await authAPI.beginWebAuthnRegistration()

          // Prompt user to create passkey
          const credential = await startRegistration(options)

          // Complete registration
          await authAPI.completeWebAuthnRegistration(credential)

          set({ isLoading: false })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Passkey registration failed',
          })
          throw error
        }
      },

      // Set onboarding step
      setOnboardingStep: (step: number) => {
        set({
          onboarding: {
            ...get().onboarding,
            step,
          },
        })
      },

      // Reset onboarding state
      resetOnboarding: () => {
        set({ onboarding: initialOnboardingState })
      },

      // Complete MFA challenge
      completeMFA: async (code: string) => {
        set({ isLoading: true, error: null })
        const { mfaChallengeId } = get()

        if (!mfaChallengeId) {
          set({ error: 'No MFA challenge active', isLoading: false })
          return
        }

        try {
          const result = await authAPI.completeMFA(mfaChallengeId, code)

          set({
            user: result.user,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            mfaChallengeId: null,
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'MFA verification failed',
          })
          throw error
        }
      },

      // Login with a pre-issued token (wallet approval or device QR approval)
      loginWithToken: async (token: string) => {
        set({ isLoading: true, error: null })
        try {
          const { jwtVerify } = await import('jose')
          const secret = new TextEncoder().encode(
            (import.meta.env.VITE_JWT_SECRET as string | undefined) || 'dev-secret-change-me'
          )
          const { payload } = await jwtVerify(token, secret)
          const userId = payload.sub ?? null
          if (!userId) throw new Error('Invalid token')

          // Build a minimal user object from JWT claims
          const mockUser: import('../lib/auth-api').User = {
            id:          userId,
            email:       (payload.email as string | undefined) || '',
            username:    (payload.username as string | undefined) || userId,
            displayName: (payload.displayName as string | undefined) || userId,
            verified:    true,
            createdAt:   new Date().toISOString(),
            roles:       ['user'],
          }
          set({ user: mockUser, isAuthenticated: true, isLoading: false })
        } catch {
          set({ isLoading: false, error: 'Authentication token is invalid or expired' })
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Set error
      setError: (error: string) => set({ error }),
    }),
    {
      name: 'lukhas-auth',
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
